import { Location } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  OnInit,
  inject,
  linkedSignal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { LoginFlow, Session, UpdateLoginFlowBody } from '@ory/client';

import { SharedModule } from 'src/app/core/shared.module';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxIconComponent } from '@ui/icon/icon.component';
import { VxFormFieldComponent } from '@ui/form-field/form-field.component';
import { VxInputComponent } from '@ui/input/input.component';
import { AuthRedirectService } from 'src/app/services/authentication/auth-redirect.service';
import {
  csrfToken,
  flowFromError,
  flowMessages,
  flowValue,
  oidcProviders,
} from 'src/app/services/authentication/kratos-flow';
import {
  ErrAlreadyLoggedIn,
  ErrBrowserLocationChangeRequired,
  ErrSecurityCsrfViolation,
  ErrSelfServiceFlowExpired,
  ErrTwoFARequired,
} from 'src/app/services/authentication/kratos.errors';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';
import {
  isNewlyRegistered,
  signInMethod,
} from 'src/app/services/authentication/sign-in-method';
import { ToastService } from 'src/app/services/toast/toast.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { OAuth2ConfirmLoginChallengeAction } from 'src/app/store/oauth2/oauth2.actions';
import {
  TrackUserLoggedInEventAction,
  TrackUserRegisteredEventAction,
} from 'src/app/store/tracking/tracking.actions';
import { environment } from 'src/environments/environment';
import { AuthHelpComponent } from '../components/auth-help/auth-help.component';
import { AuthShellComponent } from '../components/auth-shell/auth-shell.component';
import { KratosMessagesComponent } from 'src/app/core/components/kratos-messages/kratos-messages.component';
import { OidcButtonsComponent } from '../components/oidc-buttons/oidc-buttons.component';
import { RegisterPageState } from '../register/register.component';
import { TwoFactorComponent } from './two-factor/two-factor.component';

/** Kratos: no account for this address (password, code). */
const UNKNOWN_ACCOUNT = new Set([4000035, 4000037]);

@Component({
  selector: 'app-login-page',
  templateUrl: './login.component.html',
  imports: [
    SharedModule,
    RouterModule,
    AuthHelpComponent,
    AuthShellComponent,
    KratosMessagesComponent,
    OidcButtonsComponent,
    ReactiveFormsModule,
    VxButtonComponent,
    VxFormFieldComponent,
    VxIconComponent,
    VxInputComponent,
    TwoFactorComponent,
  ],
})
export class LoginPageComponent implements OnInit {
  private auth = inject(KratosAuthenticationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private toast = inject(ToastService);
  private authRedirect = inject(AuthRedirectService);
  private destroyRef = inject(DestroyRef);
  private translation = inject(TranslationService);
  private location = inject(Location);

  /**
   * Email and password, like the design: a password manager fills both at
   * once. An empty password signs in with an emailed code instead, which
   * every account has.
   */
  protected readonly form = new FormGroup({
    // "Change it" on the code page hands the address back as navigation state
    email: new FormControl(
      (this.router.getCurrentNavigation()?.extras.state as RegisterPageState)
        ?.email ?? '',
      { nonNullable: true, validators: [Validators.required, Validators.email] }
    ),
    password: new FormControl('', { nonNullable: true }),
  });

  /**
   * The providers of the first flow. A flow Kratos re-renders for a known
   * account may list only the ones linked to it; the others stay on offer.
   */
  protected readonly providers = linkedSignal<LoginFlow | undefined, string[]>({
    source: () => this.loginFlow(),
    computation: (flow, previous) =>
      previous?.value.length ? previous.value : flow ? oidcProviders(flow) : [],
  });

  /** "No such account" is said with a sign-up link instead (`noAccount`). */
  protected readonly messages = computed(() => {
    const flow = this.loginFlow();
    return flow
      ? flowMessages(flow).filter(message => !UNKNOWN_ACCOUNT.has(message.id))
      : [];
  });

  /** Kratos knows no account for the address. */
  protected readonly noAccount = computed(() => {
    const flow = this.loginFlow();
    return (
      !!flow &&
      flowMessages(flow).some(message => UNKNOWN_ACCOUNT.has(message.id))
    );
  });

  aal: 'aal1' | 'aal2' = 'aal1';
  returnTo = '/';

  /** The first-factor flow (password or code). */
  readonly loginFlow = signal<LoginFlow | undefined>(undefined);
  twoFAFlow?: LoginFlow;

  initialFlowLoading = true;

  isOAuth2Flow = false;
  oauth2LoginChallenge?: string;

  // when a user logs in with sso he will be redirected to kratos self service first
  // to complete the login flow. After that he will be redirected back to our login page.
  // the check parameter is used to check if the user is then logged in.
  oauth2ReturnUrl = `${environment.frontendUrl}/login?check=whoami`;

  /** `check=whoami`: the session from a finished login is being checked. */
  readonly checkingSession = signal(false);

  /**
   * AuthGuard sent the browser here because the app could not load for the
   * session (API down, no user): going straight back would loop.
   */
  private readonly appFailed = !!(
    this.router.getCurrentNavigation()?.extras.state as
      | { appFailed?: boolean }
      | undefined
  )?.appFailed;

  /** The session is fine, but the app could not load for it: ask. */
  protected readonly loadFailed = signal(false);

  /** "Use a different account" is ending the half-finished login. */
  protected readonly signingOut = signal(false);

  /** The address of a half-finished login, shown at the second factor. */
  protected readonly twoFAEmail = signal('');

  isCompletingLoginFlow = signal(false);

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      // redirect to returnTo parameter after successful login
      if (params['returnTo']) {
        this.returnTo = params['returnTo'];
      }

      if (params['login_challenge']) {
        this.useLoginChallenge(params['login_challenge']);
      }

      const justSignedIn = params['check'] === 'whoami';
      if (params['flow'] && !justSignedIn) {
        this.startLoginFlow(params);
        return;
      }
      // Without a flow to continue, the session decides:
      // - a finished one goes on (a login that just ended is recorded),
      // - a half-finished one (2FA still due, e.g. after leaving the second
      //   step) continues at the second factor instead of a re-auth form,
      // - none shows the form.
      // Back from a code or social login the form stays hidden meanwhile.
      this.checkingSession.set(justSignedIn);
      this.auth
        .setSessionFromWhoami()
        .then(session => {
          if (this.appFailed) {
            this.checkingSession.set(false);
            this.loadFailed.set(true);
          } else if (justSignedIn) {
            this.onLoginCompleted(session);
          } else {
            this.continueSignedIn();
          }
        })
        .catch(error => {
          this.checkingSession.set(false);
          this.startLoginFlow(
            error instanceof ErrTwoFARequired
              ? { ...params, aal: 'aal2' }
              : params
          );
        });
    });
  }

  /**
   * Already signed in: confirm an OAuth2 challenge (a status page admin sent
   * here from the dashboard) or go on to `returnTo`. Not recorded as a login.
   */
  private continueSignedIn(): void {
    if (this.isOAuth2Flow) {
      this.store.dispatch(
        new OAuth2ConfirmLoginChallengeAction(this.oauth2LoginChallenge!)
      );
    } else {
      this.router.navigate([this.returnTo]);
    }
  }

  /** After the app failed to load: try it again with the same session. */
  protected retryApp(): void {
    this.loadFailed.set(false);
    this.router.navigate([this.returnTo]);
  }

  /**
   * Ends the session (half-finished, or one the app could not load for) and
   * starts over, so someone else can sign in on this browser.
   */
  protected async useOtherAccount(): Promise<void> {
    if (this.signingOut()) {
      return;
    }
    this.signingOut.set(true);
    await this.auth.logout();
    this.signingOut.set(false);
    this.loadFailed.set(false);
    this.aal = 'aal1';
    this.twoFAFlow = undefined;
    this.loginFlow.set(undefined);
    this.form.reset();
    // keep where to go afterwards, drop the finished flow
    const params = this.restartParams();
    this.location.replaceState(
      this.router.createUrlTree(['/login'], { queryParams: params }).toString()
    );
    this.startLoginFlow(params);
  }

  /** Loads the Kratos flow the URL asks for (existing flow, 2FA, or a fresh password flow). */
  private startLoginFlow(params: Params) {
    if (params['flow']) {
      this.auth
        .getLoginFlow(params['flow'])
        .then(flow => {
          if (flow.requested_aal === 'aal2') {
            this.aal = 'aal2';
            this.useTwoFAFlow(flow);

            // when the user has enabled two factor authentication kratos self service
            // will redirect to /login?flow=xxx as the login flow is not yet completed.
            // however in this case there will be a return_to parameter which contains the
            // original oauth2 login challenge. we switch to the oauth2 flow in this case.
            if (flow.return_to) {
              const url = new URL(flow.return_to);
              const loginChallenge = url.searchParams.get('login_challenge');
              if (loginChallenge) {
                this.useLoginChallenge(loginChallenge);
              }
            }
          } else {
            this.useLoginFlow(flow);
          }

          this.initialFlowLoading = false;
        })
        .catch(() => {
          this.router.navigate(['/login']);
        });
    } else {
      // check wether we need assurance level 1 (password) or level 2 (two factor)
      if (params['aal'] && params['aal'] === 'aal2') {
        this.aal = 'aal2';

        // get two factor flow
        this.auth
          .createLoginFlow('aal2')
          .then(twoFAFlow => {
            this.initialFlowLoading = false;
            this.useTwoFAFlow(twoFAFlow);
          })
          .catch(error => this.onFlowNotCreated(error));
      } else {
        // get password flow
        this.auth
          .createLoginFlow('aal1', { returnTo: this.oauth2ReturnUrl })
          .then(flow => {
            this.initialFlowLoading = false;
            this.useLoginFlow(flow);
          })
          .catch(error => this.onFlowNotCreated(error));
      }
    }
  }

  /** Shows the second factor, naming the account it signs in to. */
  private useTwoFAFlow(flow: LoginFlow): void {
    this.twoFAFlow = flow;
    this.twoFAEmail.set(flowValue(flow, 'identifier'));
  }

  /**
   * No flow came: a session appeared meanwhile (go on), Kratos sends the
   * browser elsewhere (follow), a second factor was asked for without a
   * first one (start over at the form), or Kratos is unreachable (say so,
   * with a retry).
   */
  private onFlowNotCreated(error: unknown): void {
    if (error instanceof ErrAlreadyLoggedIn) {
      this.continueSignedIn();
    } else if (error instanceof ErrBrowserLocationChangeRequired) {
      this.authRedirect.redirect(error.redirectUri);
    } else if (this.aal === 'aal2') {
      this.aal = 'aal1';
      this.startLoginFlow(this.restartParams());
    } else {
      this.toast.showInfo(
        'pages.login.errors.flowFailed.header',
        'pages.login.errors.flowFailed.body',
        undefined,
        {
          tone: 'danger',
          action: {
            label: 'pages.login.errors.flowFailed.retry',
            handler: () => this.startLoginFlow(this.restartParams()),
          },
        }
      );
    }
  }

  /**
   * Acting as the OAuth2 provider: the challenge has to come back with every
   * login that leaves the page (code, social), so Kratos returns with it.
   */
  private useLoginChallenge(challenge: string): void {
    this.isOAuth2Flow = true;
    this.oauth2LoginChallenge = challenge;
    this.oauth2ReturnUrl = `${environment.frontendUrl}/login?login_challenge=${challenge}&check=whoami`;
  }

  /**
   * The parameters a fresh login keeps: where to go afterwards and the
   * OAuth2 challenge, which a 2FA flow may have carried instead of the URL.
   */
  private restartParams(): Params {
    const { returnTo, login_challenge } = this.route.snapshot.queryParams;
    return {
      returnTo,
      login_challenge: login_challenge ?? this.oauth2LoginChallenge,
    };
  }

  /**
   * Shows `flow`. Linking a provider to an existing account comes back as
   * a flow for that address: the person proves it's theirs first.
   */
  private useLoginFlow(flow: LoginFlow): void {
    this.loginFlow.set(flow);
    const identifier = flowValue(flow, 'identifier');
    if (identifier) {
      this.form.controls.email.setValue(identifier);
    }
  }

  /** With a password, signs in with it; without, emails a login code. */
  protected async submit(): Promise<void> {
    const flow = this.loginFlow();
    const { email, password } = this.form.controls;
    if (!flow || this.isCompletingLoginFlow()) {
      return;
    }
    if (email.invalid) {
      email.markAsTouched();
      return;
    }
    if (password.value) {
      await this.submitFlow({
        method: 'password',
        identifier: email.value.trim(),
        password: password.value,
        csrf_token: csrfToken(flow),
      });
      return;
    }
    this.isCompletingLoginFlow.set(true);
    const next = await this.step1(flow, {
      method: 'code',
      identifier: email.value.trim(),
      csrf_token: csrfToken(flow),
      transient_payload: this.transientPayload(flow.id),
    });
    this.isCompletingLoginFlow.set(false);
    if (next?.state === 'sent_email') {
      this.openCodePage(next);
    } else if (next) {
      this.useLoginFlow(next);
    }
  }

  protected signInWith(provider: string): void {
    const flow = this.loginFlow();
    if (!flow) {
      return;
    }
    this.submitFlow({
      method: 'oidc',
      provider,
      csrf_token: csrfToken(flow),
    });
  }

  /** A code request re-renders the flow, which comes back as a 400. */
  private async step1(
    flow: LoginFlow,
    body: UpdateLoginFlowBody
  ): Promise<LoginFlow | null> {
    try {
      const session = await this.auth.completeLoginFlowV2(
        flow,
        body.method,
        body
      );
      this.onLoginCompleted(session);
      return null;
    } catch (err) {
      const next = flowFromError<LoginFlow>(err);
      if (!next) {
        this.toast.showInfo(
          'pages.login.errors.sessionExpired.header',
          'pages.login.errors.sessionExpired.body'
        );
      }
      return next;
    }
  }

  /** The code page hands back here when done, so it carries `returnTo`. */
  private openCodePage(flow: LoginFlow): void {
    this.router.navigate(['/register', 'code'], {
      queryParams: {
        flow: flow.id,
        returnTo: this.returnTo === '/' ? undefined : this.returnTo,
      },
    });
  }

  private transientPayload(flowId: string) {
    return { flow_id: flowId, locale: this.translation.currentLocale };
  }

  async submitFlow(data: UpdateLoginFlowBody) {
    if (this.isCompletingLoginFlow()) {
      return;
    }
    this.isCompletingLoginFlow.set(true);

    let isRedirecting = false;
    await this.auth
      .completeLoginFlowV2(this.loginFlow()!, data.method, data)
      .then(session => {
        this.onLoginCompleted(session);
      })
      .catch(err => {
        if (err instanceof ErrTwoFARequired) {
          this.router.navigate(['/login'], { queryParams: { aal: 'aal2' } });
        } else if (err instanceof ErrBrowserLocationChangeRequired) {
          isRedirecting = true;
          this.authRedirect
            .redirect(err.redirectUri)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this.isCompletingLoginFlow.set(false);
            });
        } else if (
          err instanceof ErrSecurityCsrfViolation ||
          err instanceof ErrSelfServiceFlowExpired
        ) {
          this.toast.showInfo(
            'pages.login.errors.sessionExpired.header',
            'pages.login.errors.sessionExpired.body'
          );
        } else {
          const next = flowFromError<LoginFlow>(err);
          if (next) {
            this.useLoginFlow(next);
          }
        }
      })
      .finally(() => {
        // stay busy while the browser leaves for the provider: another
        // provider submit now would invalidate this redirect's OIDC state
        if (!isRedirecting) {
          this.isCompletingLoginFlow.set(false);
        }
      });
  }

  onLoginCompleted(session: Session) {
    const method = signInMethod(session);

    // An OIDC sign-up creates the identity together with the session; the
    // action=register return URL alone also comes back for existing accounts.
    if (isNewlyRegistered(session)) {
      this.store.dispatch(
        new TrackUserRegisteredEventAction(session.identity!.id, method)
      );
    }

    this.store.dispatch(
      new TrackUserLoggedInEventAction(session.identity!.id, method)
    );

    if (this.isOAuth2Flow) {
      this.store.dispatch(
        new OAuth2ConfirmLoginChallengeAction(this.oauth2LoginChallenge!)
      );
    } else {
      this.router.navigate([this.returnTo]);
    }
  }
}
